/* ------------------------------------------------------------------------
 * DAF client: extract, site fetch, profile, vertical, BOP
 *
 * Lifted VERBATIM from the single-file build — not rewritten, not tidied.
 * The detector behaviour in here is the product, and it carries a long tail
 * of corrections derived from real policy paper: a London Market Reform
 * Contract stating cover as sums insured; triggers stated in a declarations
 * table; the rule that a limit is a multiple of 500 and at least $1,000, so a
 * policy number cannot be printed as a limit; a declarations schedule vetoing
 * its own policy wording. 1,053 assertions cover that behaviour and they only
 * keep covering it while the code stays byte-identical.
 *
 * Cross-module calls still go through the `window.*` registry the original
 * used. That is deliberate: rewiring ~190 functions into an import graph is a
 * separate change with its own risk, and this one moves the code and nothing
 * else. See MIGRATION.md.
 *
 * From index.html, script block 6 (781 lines).
 * ---------------------------------------------------------------------- */


import { getToken } from './session.js';

(function(){
  // '' is meaningful: it means same-origin (dev proxy), so don't treat it as unset.
  const _b=(window.SENTRIPOL_CONFIG||{}).backend;
  const DAF_BASE=(_b==null?'http://localhost:8000':_b).replace(/\/+$/,'');
  let _tok=null;
  // fetch with a hard timeout so a slow/hung backend can never freeze the UI
  async function _fetchT(url, opts, ms){
    const c=new AbortController(); const t=setTimeout(()=>c.abort(), ms||90000);
    try { return await fetch(url, Object.assign({}, opts, {signal:c.signal})); }
    finally { clearTimeout(t); }
  }
  // Sentripol BE authenticates to DAF server-side, so the browser never holds a
  // DAF credential. config.js used to ship an email and a password to every
  // visitor and this function used them — anyone with the URL had the account.
  //
  // What the header carries now is the BE session the access gate obtained, not
  // a DAF token: BE requires one on every route below and attaches its own DAF
  // credential on the far side. This is the seam that was left here for "real
  // auth when it arrives", and it arrived. Returning '' would 401 every call.
  async function _login(){ return await getToken() || ''; }
  // Server-side OCR/extraction — offloads heavy OCR off the browser thread
  // (in-browser Tesseract freezes the tab on large scans). Returns {text,...}.
  window.spBackendExtract = async function(file, maxOcrPages){
    const token = await _login();
    const fd = new FormData();
    fd.append('file', file);
    fd.append('max_ocr_pages', String(maxOcrPages || 15));
    // Policy review wants LlamaParse's layout/table extraction on declarations
    // pages and forms schedules, not pypdf's flat text. 'auto' only reaches for
    // LlamaParse on scanned pages, so ask for it explicitly. Results are cached
    // by content hash server-side, so re-uploads cost no credits.
    fd.append('engine', ((window.SENTRIPOL_CONFIG||{}).extractEngine||'llamaparse'));
    /* 510s, and the chain behind it is 480s then 450s.
     *
     * All three were 150s, which meant they expired together and the longest
     * document in a program was the one guaranteed to fail: a 133-page policy
     * aborted here, fell back to in-browser pdf.js, and cost four minutes to
     * produce a worse answer than the backend would have. A parse allowed to
     * finish is cached server-side by content hash, so the wait is paid once
     * per document, ever. */
    const r = await _fetchT(DAF_BASE + '/api/files/extract', { method:'POST', headers:{ ...(token ? {'Authorization':'Bearer '+token} : {}) }, body: fd }, 510000);
    if (!r.ok) throw new Error('extract ' + r.status);
    return await r.json();
  };

  // Real company-website fetch. Browsers can't cross-origin fetch an arbitrary
  // site, and the server-side fetcher is address-gated (no private/loopback
  // targets), so this is the only path that yields INDEPENDENT company text —
  // the evidence spSiteCrossCheck needs to confirm or contradict a vertical.
  window.spFetchSite = async function(url, followAbout){
    const token = await _login();
    const r = await _fetchT(DAF_BASE+'/api/fetch/site', {
      method:'POST', headers:{...(token ? {'Authorization':'Bearer '+token} : {}),'Content-Type':'application/json'},
      body: JSON.stringify({ url: url, follow_about: followAbout!==false })
    }, 45000);
    if(!r.ok){
      let msg = 'fetch '+r.status;
      try { const j = await r.json(); if(j && j.detail) msg = j.detail; } catch(_){}
      throw new Error(msg);
    }
    return await r.json();
  };

  const _norm = s => String(s||'').toLowerCase().replace(/["“”'']/g,'').replace(/\s+/g,' ').trim();

  // Line of Business of the current review ('pc' | 'eb' | '').
  function _spLob(){
    return ((window._lastFormData && window._lastFormData.lob) ||
            (document.getElementById('lob') && document.getElementById('lob').value) || '').toLowerCase();
  }
  // Keep only mandates whose category matches the reviewed LOB. P&C reviews
  // therefore drop EB mandates (ACA/ERISA/COBRA/PFML) and vice-versa; unknown
  // LOB shows all.
  function _spFilterMandates(sm, lob){
    if(!sm) return {};
    if(lob!=='pc' && lob!=='eb') return sm;
    const out={};
    Object.keys(sm).forEach(k=>{ const arr=(sm[k]||[]).filter(m=>((m.cat||'eb')===lob)); if(arr.length) out[k]=arr; });
    return out;
  }

  // Confirm an LLM-claimed item is real: its evidence quote must actually appear
  // in a source (verbatim-ish), OR the vertical's known keywords must appear.
  // Returns {source, evidence} or null. This is the anti-hallucination gate.
  // Evidence that cannot support an operating state, whatever it says.
  //
  // The sources are stripped of review cards before the model sees them, and
  // the prompt forbids these outright, but neither is a guarantee: a model can
  // return a quote it assembled rather than copied, and a stripper only
  // removes shapes it recognises. This is the last gate, and unlike the other
  // two it is deterministic.
  //
  // Real examples that reached a report: "4.8 stars Bob A. at home in
  // Waxahachie, Texas · Waxahachie, TX Read Bob's story" — a customer's home,
  // not an operation — and "| Mailing Address: P.O. Box 420029 Houston, Texas
  // 77242 |", a claims mail drop printed on every policy.
  const SP_UNUSABLE_EVIDENCE = [
    /\b\d(?:\.\d)?\s*(?:\/\s*5\s*)?stars?\b/i,
    /\bread\s+[a-z]+(?:'s|\u2019s)?\s+(?:full\s+)?stor(?:y|ies)\b/i,
    /\b[a-z]+\s+[a-z]\.\s*(?:at\s+home\s+in|,|\u00b7|\u2022)/i,
    /\bat\s+home\s+in\b/i,
    /\bverified\s+(?:customer|buyer|purchaser|reviewer)\b/i,
    /\bp\.?\s*o\.?\s*box\b/i,
    /\btestimonial|\breview(?:ed|er)?\b\s+(?:by|from)\b/i
  ];

  function _spEvidenceUsable(quote){
    const q=String(quote||'');
    return !SP_UNUSABLE_EVIDENCE.some(re=>re.test(q));
  }

  function _groundClaim(name, evidenceQuote, sources){
    // A quote that carries the marks of a review card or a mail drop is
    // rejected before it is even looked for in the sources: finding it there
    // would only prove the page contains a testimonial.
    if(!_spEvidenceUsable(evidenceQuote)) return null;
    const q=_norm(evidenceQuote);
    if(q && q.length>=10){
      for(const src of sources){
        const hay=_norm(src.text);
        // try full quote, then a long window of it (LLM may trim/expand slightly)
        const probe = q.length>90 ? q.slice(0,90) : q;
        if(hay.includes(probe) || (q.length>24 && hay.includes(q.slice(0,24)))){
          // recover a readable snippet from the ORIGINAL text
          const at=(src.text||'').toLowerCase().indexOf((evidenceQuote||'').toLowerCase().replace(/["“”'']/g,'').trim().slice(0,20));
          const raw=src.text||''; const s=Math.max(0,at-30), e=Math.min(raw.length, (at<0?0:at)+120);
          const snip=(at<0? evidenceQuote : (s>0?'…':'')+raw.slice(s,e).replace(/\s+/g,' ').trim()+(e<raw.length?'…':''));
          return { source: src.label, evidence: snip };
        }
      }
    }
    // fallback: keyword grounding for known verticals
    const kw=(window.SP_VERTICAL_KEYWORDS||{})[name];
    if(kw){ const g=window.spVerifyGrounding(kw, sources); if(g) return { source:g.source, evidence:g.evidence }; }
    return null;
  }

  // Intelligent path: LLM classifies, then EVERY item is grounded here.
  window.spDetectProfileAI = async function(sources){
    // Send the strongest, bounded slice of evidence (declarations + website + file names first).
    const ordered=[...sources].sort((a,b)=>(b.weight||1)-(a.weight||1));
    let text=''; for(const s of ordered){ if(text.length>55000) break; text+='\n['+s.label+']\n'+(s.text||'').slice(0,20000); }
    const token=await _login();
    const r=await _fetchT(DAF_BASE+'/api/analyze/profile',{method:'POST',headers:{...(token ? {'Authorization':'Bearer '+token} : {}),'Content-Type':'application/json'},
      body:JSON.stringify({ text, verticals_catalog:Object.keys(window.SP_VERTICAL_KEYWORDS||{}) })});
    if(!r.ok) throw new Error('profile '+r.status);
    const data=await r.json();
    const verticals=[];
    (data.verticals||[]).forEach(v=>{
      const g=_groundClaim(v.name, v.evidence, sources);
      if(g) verticals.push({ name:v.name, evidence:g.evidence, source:g.source, matched:(v.evidence||v.name) });
    });
    // States: the frequency/context/boilerplate-aware heuristic is MORE robust
    // than the LLM here — the LLM can mistake a carrier/agent address
    // ("Naperville, IL 60563") or an ISO form prefix ("IL 83 00 TX") for an
    // operating state, and grounding can't reject it (the token is present).
    // So use the grounded heuristic for states; keep the LLM for verticals.
    // Same floor as the heuristic path, scored against the LLM's OWN top pick:
    // the model still decides what belongs on the list, the floor only decides
    // how far down it runs. Its leading vertical is always 100 and always kept,
    // so a correct-but-keyword-thin classification is never thrown away.
    /* Through the registry, because the scorer is not in this scope.
     *
     * _spScoreVertical is declared inside grounding.js's closure and published
     * as window.spScoreVertical. A bare read of the underscore name here is a
     * ReferenceError, not an undefined — so the whole AI vertical path threw
     * on every run and fell back to the heuristic, silently apart from one
     * console line. The DAF classification the model was asked for never
     * reached the report.
     *
     * This is the second time a name that lives in another module's closure
     * has been read directly. The registry is the contract between them. */
    const _score = (typeof window !== 'undefined' && window.spScoreVertical) || null;
    if (!_score) throw new Error('spScoreVertical unavailable — grounding.js did not load');
    verticals.forEach(v=>{ v.score = _score(v.name, sources).score; });
    const aiMax = verticals.reduce((m,v)=>Math.max(m, v.score), 0);
    verticals.forEach(v=>{ v.confidence = aiMax ? Math.round((v.score / aiMax) * 100) : 100; });
    const kept = verticals.filter(v=>v.confidence >= 50);

    const hprof = window.spDetectProfile(sources) || {};
    return { verticals:(kept.length ? kept : verticals.slice(0,1)).slice(0,4),
             states:(hprof.states||[]).slice(0,4),
             policyOnlyStates:hprof.policyOnlyStates||[], model:data.model };
  };

  // ADDRESS ROLES — the one judgement the deterministic reader cannot make.
  //
  // The model is asked what each postal address IS, not which states the
  // company operates in. Everything it returns is then checked by
  // spVerifyLocations: the address has to be in the site text, and its ZIP has
  // to match its state. See packages/engine/src/locations.js for why the
  // contract is shaped that way — in short, a hallucinated STATE cannot be
  // falsified by quote-matching, and a hallucinated ADDRESS can.
  //
  // Site text only. An address on policy paper is as likely to be the
  // carrier's, the agent's or a mortgagee's, and states do not come from paper.
  window.spClassifyLocations = async function(sources){
    const site=(sources||[]).filter(s=>s.kind==='site');
    if(!site.length) return { locations:[], model:null };
    let text=''; for(const s of site){ if(text.length>40000) break; text+='\n['+s.label+']\n'+(s.text||'').slice(0,20000); }
    if(!text.trim()) return { locations:[], model:null };
    const token=await _login();
    const r=await _fetchT(DAF_BASE+'/api/analyze/locations',{method:'POST',
      headers:{...(token ? {'Authorization':'Bearer '+token} : {}),'Content-Type':'application/json'},
      body:JSON.stringify({ text })});
    if(!r.ok) throw new Error('locations '+r.status);
    return await r.json();
  };

  function _stateNames(){
    const out={};
    const sp=(typeof STATE_PROFILES!=='undefined') ? STATE_PROFILES : window.STATE_PROFILES;
    if(sp) Object.values(sp).forEach(s=>{ if(s && s.abbr) out[s.abbr]=s.name; });
    return out;
  }

  /** States from verified addresses, folded into the states already found.
   *
   *  Never throws and never blocks: a failure here leaves the deterministic
   *  result exactly as it was, which is the result the product shipped with. */
  async function _withVerifiedAddresses(sources, states){
    try{
      const res=await window.spClassifyLocations(sources);
      const locs=(res && res.locations) || [];
      if(!locs.length) return { states, addressModel:res && res.model, verified:[], rejected:[] };
      const v=window.spVerifyLocations(locs, sources);
      const merged=window.spMergeLocationStates(states, v.accepted, _stateNames());
      return { states:merged, addressModel:res.model, verified:v.accepted, rejected:v.rejected };
    }catch(e){
      console.warn('[locations] address classification unavailable:', e && e.message);
      return { states, addressModel:null, verified:[], rejected:[] };
    }
  }

  // Try the intelligent path; fall back to the deterministic grounded heuristic.
  window.spDetectProfileSmart = async function(sources){
    let verticals=null, method='', model=null;
    try{
      const ai=await window.spDetectProfileAI(sources);
      if(ai && ai.verticals.length){
        verticals=window.spSiteCrossCheck(ai.verticals, sources);
        model=ai.model;
        method='AI ('+(ai.model||'llm')+'), grounded + site cross-checked';
      } else {
        console.warn('[profile] AI returned nothing grounded; using heuristic');
      }
    }catch(e){ console.warn('[profile] AI path failed, using heuristic:', e && e.message); }

    // The deterministic pass runs either way: it owns the states, and on the
    // AI path it owned them already (spDetectProfileAI returns hprof.states).
    const h=window.spDetectProfile(sources);
    if(!verticals){
      verticals=window.spSiteCrossCheck(h.verticals, sources);
      method='heuristic (grounded fallback) + site cross-checked';
    }
    const loc=await _withVerifiedAddresses(sources, h.states);
    return { verticals, states:loc.states, policyOnlyStates:h.policyOnlyStates||[],
             verifiedAddresses:loc.verified, rejectedAddresses:loc.rejected,
             addressModel:loc.addressModel,
             method: method + (loc.verified.length ? ', addresses verified' : '') };
  };

  /* The shape the panel renders when the classification did not happen.
   *
   * Same field names the endpoint returns, so spRenderNaicsPanel needs no
   * special case beyond reading naics.source. Everything derived from the
   * classification is empty here BECAUSE it was never derived, which the panel
   * states rather than implying the policy lacks it. */
  window.spNaicsUnavailable = function(detail){
    return {
      naics: { code:null, sic:null, source:'unavailable', detail:detail||null, evidence:null, rollup:[], vertical:null },
      carriers: [], forms_schedule: { lines:[], other_total:0, other_prefixes:[], total_forms:0 },
      federal_frameworks: [], state_mandates: {}, case_law: [], compliance_snapshot: {},
      _unavailable: true
    };
  };

  // ===== NAICS-pivot vertical analysis (memo architecture) =====
  /* THE LINE THAT ANSWERS THE QUESTION GOES FIRST.
   *
   * Matrix Human Services' package policy says, on its declarations:
   *
   *     DESCRIPTION OF BUSINESS
   *     Business Description: Non-Profit with Social Service Agencies
   *
   * The classifier never saw it. spBuildSources cuts a "declarations" source
   * from the first 6,000 characters of each file and that line sits at 12,944;
   * the full-document source that does contain it never fit, because the
   * 55,000-character budget was spent on the website, three declaration heads
   * and the whole of the Cyber policy. So the model was asked to infer the
   * trade of a Head Start operator from a cyber policy, and whatever it
   * returned was then verified against the same starved text.
   *
   * A withdrawal is the right outcome for a model that guesses. It is the
   * wrong outcome for a model that was never shown the answer, and the two are
   * indistinguishable in the report.
   *
   * spReadBusinessDescription already finds these fields across every source
   * for the vertical classifier. Prepending them costs a few hundred
   * characters and guarantees the one line that settles the question survives
   * any budget.
   */
  function _declaredBlock(sources){
    if (typeof window.spReadBusinessDescription !== 'function') return '';
    const seen=new Set(), lines=[];
    (window.spReadBusinessDescription(sources)||[]).forEach(r=>{
      const v=r.code ? ('NAICS/SIC '+r.code) : r.value;
      if(!v) return;
      const k=(r.label||'')+'|'+v;
      if(seen.has(k)) return; seen.add(k);
      lines.push('- '+v+'   [stated on '+(r.label||'the declarations')+']');
    });
    if(!lines.length) return '';
    return '[DECLARED BUSINESS DESCRIPTION — read from a labelled field on the '
         + 'declarations, which is the carrier\u2019s own statement of the insured\u2019s '
         + 'trade]\n' + lines.slice(0,12).join('\n') + '\n';
  }

  window.spNaicsAnalyze = async function(sources, states){
    const ordered=[...sources].sort((a,b)=>(b.weight||1)-(a.weight||1));
    let text=_declaredBlock(sources);
    for(const s of ordered){ if(text.length>55000) break; text+='\n['+s.label+']\n'+(s.text||'').slice(0,25000); }
    const token=await _login();
    /* Never throw for a service failure — return the fault instead.
     *
     * A throw left window._naicsAnalysis null, and spRenderNaicsPanel(null)
     * returns early, so the entire NAICS panel vanished from the report with
     * only a console warning. An absent section and an absent NAICS are both
     * silent, and neither is true. Returning the shape means the panel renders
     * and says what happened. */
    let r;
    try {
      /* The declared business descriptions go as their own field, not just
       * buried in `text`. They decide the NAICS when they exist — the carrier's
       * own statement of what it underwrote outranks anything inferred from a
       * document set, which is the same order of authority the vertical
       * classifier uses. Sending them inline only ever made them survivable;
       * sending them as a field makes them authoritative. */
      const declared=(window.spReadBusinessDescription ? window.spReadBusinessDescription(sources) : [])
        .filter(r=>r.value)
        .map(r=>({ value:r.value, label:r.label, evidence:r.evidence }))
        .slice(0,12);
      r = await _fetchT(DAF_BASE+'/api/analyze/vertical',{method:'POST',headers:{...(token ? {'Authorization':'Bearer '+token} : {}),'Content-Type':'application/json'},
        body:JSON.stringify({ text, states:states||[], derive_stack:true, declared_business:declared })});
    } catch(e){
      return window.spNaicsUnavailable('the classifier could not be reached: '+((e&&e.message)||e));
    }
    if(!r.ok){
      let why='HTTP '+r.status;
      try { const b=await r.json(); if(b&&(b.detail||b.error)) why=String(b.detail||b.error); } catch(_e){}
      return window.spNaicsUnavailable(why);
    }
    return await r.json();
  };

  /* Where a verified quote sits, using the same declarations reader the vertical
   * classifier uses. 'declared' means the quote overlaps a labelled Business
   * Description / Nature of Business field — the carrier's own statement of the
   * trade — which is the strongest position a quote can have on policy paper. */
  window.spNaicsQuotePosition = function(quote){
    const q = String(quote||'').replace(/\s+/g,' ').trim().toLowerCase();
    if (q.length < 8) return null;
    const srcs = window._spSources || [];
    if (!srcs.length || !window.spReadBusinessDescription) return null;
    const fields = window.spReadBusinessDescription(srcs);
    for (const f of fields){
      const ev = String(f.evidence||'').replace(/\s+/g,' ').trim().toLowerCase();
      const val = String(f.value||'').replace(/\s+/g,' ').trim().toLowerCase();
      if (!ev) continue;
      // The quote contains the field's own evidence, or the field's stated
      // value appears inside the quote. Either way they are the same place.
      if (ev.includes(q) || q.includes(ev) || (val.length >= 4 && q.includes(val))) return 'declared';
    }
    return 'text';
  };

  window.spRenderNaicsPanel = function(data){
    if(!data) return;
    const esc=s=>{const d=document.createElement('div');d.textContent=String(s==null?'':s);return d.innerHTML;};
    const n=data.naics||{};
    const chip=(t,c)=>'<span style="display:inline-block;padding:2px 8px;border-radius:10px;background:'+c+';color:#fff;font-size:12px;margin:2px;">'+esc(t)+'</span>';
    let h='<div id="spNaicsPanel" style="border:1px solid #00695c;border-radius:8px;padding:12px 14px;margin:0 0 16px;background:#f3fbf9;">';
    h+='<div style="font-weight:700;color:#00695c;margin-bottom:6px;">🧭 NAICS Vertical Analysis '
      +'<span style="font-weight:400;font-size:11px;color:#777;">— classify by NAICS, derive from policy + reference library (lib v'+esc((data.compliance_snapshot||{}).library_version)+')</span></div>';
    // NAICS
    if(n.code){
      /* WHERE the supporting quote sits decides how much it is worth.
       *
       * The service checked that the quote is really in the text, that the code
       * is a real NAICS code, and that the two agree. What it cannot check is
       * the one thing this page knows: whether the quote lands in a labelled
       * declarations field — the carrier's own statement of the trade — or in
       * running text, which is a weaker thing even when it is true. */
      const pos = window.spNaicsQuotePosition ? window.spNaicsQuotePosition(n.evidence) : null;
      const vpill = (bg,bd,fg,txt,tip)=>' <span title="'+tip+'" style="font-size:10px;color:'+fg
        +';background:'+bg+';border:1px solid '+bd+';border-radius:8px;padding:1px 6px;">'+txt+'</span>';
      const vb = n.source==='declarations'
          ? vpill('#e8f5e9','#a5d6a7','#1b5e20','printed on the policy','The code is printed on the declarations — read, not inferred')
        : pos==='declared'
          ? vpill('#e8f5e9','#a5d6a7','#1b5e20','✓ verified — declarations field','The quote was found in the documents, in a labelled Business Description / Nature of Business field, and its trade agrees with the code\u2019s NAICS sector')
        : (n.verification && n.verification.quote_found)
          ? vpill('#ede7f6','#b39ddb','#4527a0','✓ verified — quoted in the documents','The quote was found verbatim in the documents and its trade agrees with the code\u2019s NAICS sector, but it is running text rather than a labelled declarations field')
        :   vpill('#f5f5f5','#e0e0e0','#616161','unchecked','This code arrived without a verification record \u2014 an older service version');
      h+='<div style="margin:6px 0;"><strong>NAICS:</strong> '+chip(n.code,'#00695c')
        +' <span style="font-size:12px;color:#555;">('+esc(n.source)+')</span>'+vb+' → <strong>'+esc(n.vertical||'—')+'</strong>'
        +'<div style="font-size:11px;color:#777;margin-top:2px;">roll-up: '+(n.rollup||[]).join(' → ')
        + (n.evidence?' · evidence: <em>'+esc(n.evidence)+'</em>':'')+'</div></div>';
    } else if(n.source === 'unverified'){
      /* The classifier answered and the answer did not survive checking. That
       * is neither a service fault nor a finding about the documents, and
       * saying either would be wrong: the third thing is that we had an answer
       * and would not stand behind it. The withdrawn code is shown, because a
       * broker who re-runs this and gets the same code deserves to know it was
       * seen and rejected rather than never produced. */
      h+='<div style="margin:6px 0;padding:8px 10px;border-left:3px solid #6a1b9a;background:#f5f0fa;">'
        +'<strong>A NAICS code was proposed and withdrawn.</strong>'
        +'<div style="font-size:11px;color:#555;margin-top:3px;">'
        +'The classifier returned '+esc(n.withdrawn_code||'a code')+', and it did not survive verification: '
        +esc(n.detail||'the supporting quote could not be confirmed')+'. '
        +(n.partial_code?'The declarations print NAICS '+esc(n.partial_code)+'. ':'')
        +'A code is only reported when its supporting quote is found verbatim in these documents and the '
        +'trade it names agrees with the code\u2019s NAICS sector.'
        +(n.withdrawn_evidence?'<br><span style="color:#777;">Quote offered: <em>'+esc(n.withdrawn_evidence)+'</em></span>':'')
        +'</div></div>';
    } else if(n.source === 'declarations-partial'){
      /* The paper names the industry FAMILY even though nobody could name the
       * industry. Reporting nothing would discard a real fact, so this says
       * what is known and what it is not enough for. */
      h+='<div style="margin:6px 0;padding:8px 10px;border-left:3px solid #B8860B;background:#fff8e1;">'
        +'<strong>NAICS '+esc(n.partial_code||'')+' is printed, but it is not a full code.</strong>'
        +'<div style="font-size:11px;color:#555;margin-top:3px;">'+esc(n.detail||'')
        +' The classifier could not resolve it to six digits from the policy text either. '
        +'The coverage stack is derived from the six-digit industry, so it is not derived here.</div></div>';
    } else if(n.source === 'unavailable'){
      /* The classifier could not run. This is a service problem and must not be
       * written as a fact about the insured's paperwork — which is exactly what
       * this branch used to do: one sentence covered a missing environment
       * variable and a policy with no NAICS on it, and the Base Power review
       * reported the second when the truth was the first. Everything keyed off
       * the classification degrades with it, so say that too. */
      h+='<div style="margin:6px 0;padding:8px 10px;border-left:3px solid #B8860B;background:#fff8e1;">'
        +'<strong>NAICS classification unavailable \u2014 this section did not run.</strong>'
        +'<div style="font-size:11px;color:#555;margin-top:3px;">This is a service fault, not a finding: '
        +'nothing here says the insured has no NAICS. Federal frameworks, public sources and the '
        +'vertical-gated exclusion checks all derive from the classification and are equally unevidenced '
        +'in this run. Re-run once the classifier is available before relying on this report.'
        +(n.detail?'<br><span style="color:#777;">Reported: '+esc(n.detail)+'</span>':'')
        +'</div></div>';
    } else if(n.source === 'undetermined'){
      /* Ran, and could not determine a code. That IS a finding, and is worded
       * as one: about the documents, not about the service. Only this branch
       * may claim the classifier ran, because only this source proves it did. */
      h+='<div style="margin:6px 0;color:#a00;">No NAICS code is printed on the policy declarations, '
        +'and the classifier could not determine one from the policy text or the organization profile. '
        +'<span style="font-size:11px;color:#777;">(classifier ran; result was inconclusive)</span></div>';
    } else {
      /* Anything else — including the older backend's "none", which it returns
       * for a rate limit, an unconfigured model and a genuine null alike. We
       * cannot tell whether the classifier ran, so we must not say that it did.
       * This is what a 429 on gpt-4-1 rendered as before this branch existed:
       * "classifier ran; result was inconclusive", about a run that never
       * happened. Report the uncertainty instead of resolving it in either
       * direction. */
      h+='<div style="margin:6px 0;padding:8px 10px;border-left:3px solid #B8860B;background:#fff8e1;">'
        +'<strong>No NAICS classification in this run.</strong>'
        +'<div style="font-size:11px;color:#555;margin-top:3px;">The server did not report whether the '
        +'classifier ran, so this is not evidence that the insured has no NAICS code. Anything derived '
        +'from the classification \u2014 federal frameworks, public sources, vertical-gated exclusion '
        +'checks \u2014 is unevidenced in this run. Check the service and re-run before relying on it.'
        +(n.source?' <span style="color:#777;">(reported source: '+esc(n.source)+')</span>':'')
        +'</div></div>';
    }
    // Carrier / insurer / program names (NOT the insured)
    if((data.carriers||[]).length){
      h+='<div style="margin:6px 0;"><strong>Carrier / program</strong> <span style="font-size:11px;color:#777;">(insurer — not the insured)</span>: '
        + data.carriers.map(c=>'<span style="display:inline-block;padding:2px 8px;border-radius:10px;background:#616161;color:#fff;font-size:11px;margin:2px;">'+esc(c.name)+'</span>').join('')
        + '</div>';
    }
    // Federal frameworks
    h+='<div style="margin:8px 0;"><strong>Federal frameworks</strong> (by NAICS): ';
    h+=(data.federal_frameworks||[]).length? (data.federal_frameworks.map(f=>chip(f.framework,'#00838f')).join('')) : '<span style="color:#777;">none mapped</span>';
    if((data.federal_frameworks||[]).length){ h+='<div style="font-size:11px;color:#555;margin-top:3px;">'+data.federal_frameworks.map(f=>esc(f.framework)+': '+esc(f.rule)+' <em>('+esc(f.citation)+')</em>').join('<br>')+'</div>'; }
    h+='</div>';
    // State mandates — filtered to the reviewed Line of Business
    const lob=_spLob();
    const sm=_spFilterMandates(data.state_mandates||{}, lob);
    const lobLabel = lob==='pc' ? 'P&C' : (lob==='eb' ? 'Employee Benefits' : 'all lines');
    h+='<div style="margin:8px 0;"><strong>State mandates</strong> <span style="font-size:11px;color:#777;">('+lobLabel+')</span>: ';
    if(Object.keys(sm).length){
      Object.keys(sm).forEach(st=>{ h+='<details style="margin:2px 0;background:#fff;border:1px solid #cfe9e4;border-radius:4px;"><summary style="cursor:pointer;padding:4px 8px;">'+chip(st,'#00695c')+' <span style="font-size:11px;color:#777;">'+sm[st].length+' mandate(s)</span></summary><div style="padding:4px 10px 8px;font-size:12px;">'+sm[st].map(m=>'• <strong>'+esc(m.mandate)+'</strong> — '+esc(m.trigger)+' <em>('+esc(m.citation)+')</em>').join('<br>')+'</div></details>'; });
    } else { h+='<span style="color:#777;">none for the '+esc(lobLabel)+' line in these states</span>'; }
    h+='</div>';
    // Exclusion flags (present in policy)
    // EB Sections 10 & 11 — DEMO-ONLY (shown when the Demo button is toggled on),
    // displayed alongside the P&C content.
    if(window._spDemo && data.eb_preview){
      const ep=data.eb_preview;
      const rc=r=>r==='HIGH'?'#c62828':(r==='MODERATE'?'#f9a825':'#00695c');
      h+='<div style="margin:10px 0;border:1px dashed #5b2be0;border-radius:6px;padding:8px 10px;background:#faf9ff;">';
      h+='<div style="font-weight:700;color:#5b2be0;">10. '+esc(ep.section9_title)+' <span style="font-size:10px;color:#fff;background:#f9a825;padding:1px 6px;border-radius:8px;">DEMO · EB</span></div>';
      (ep.section9||[]).forEach(f=>{ h+='<div style="margin:3px 0;font-size:12px;">• <strong>'+esc(f.factor)+'</strong> <span style="color:#fff;background:'+rc(f.risk)+';padding:1px 6px;border-radius:8px;font-size:10px;">'+esc(f.risk)+'</span> — benchmark '+esc(f.benchmark)+'<br><span style="font-size:11px;color:#555;">'+esc(f.note)+'</span></div>'; });
      h+='<div style="font-weight:700;color:#5b2be0;margin-top:8px;">11. '+esc(ep.section10_title)+' <span style="font-size:10px;color:#fff;background:#f9a825;padding:1px 6px;border-radius:8px;">DEMO · EB</span></div>';
      h+='<table style="width:100%;border-collapse:collapse;font-size:12px;margin-top:3px;">';
      (ep.section10||[]).forEach(x=>{ h+='<tr><td style="padding:3px 6px;border-bottom:1px solid #eee;">'+esc(x.enhancement)+'</td><td style="padding:3px 6px;text-align:right;font-weight:600;border-bottom:1px solid #eee;">'+esc(x.cost_per_employee)+'</td></tr>'; });
      h+='</table>';
      if(ep.note) h+='<div style="font-size:10px;color:#999;margin-top:4px;">'+esc(ep.note)+'</div>';
      h+='</div>';
    }
    // Case-law exclusions
    h+='<div style="margin:8px 0;"><strong>Case-law exclusions flagged present in this policy</strong>: ';
    h+='<div style="margin-top:3px;">'+window.spBuildExclusionsHtml(data.exclusion_flags, esc)+'</div>';
    h+='</div>';
    // Forms schedule — ISO/bureau forms in force on the policy (memo §3)
    const fs=data.forms_schedule;
    if(fs && (fs.lines||[]).length){
      h+='<div style="margin:8px 0;"><strong>Forms schedule</strong> <span style="font-size:11px;color:#777;">(ISO/bureau — '+esc(fs.total_forms)+' forms)</span>';
      fs.lines.forEach(L=>{ h+='<details style="margin:2px 0;background:#fff;border:1px solid #e3e0f5;border-radius:4px;"><summary style="cursor:pointer;padding:4px 8px;"><span style="display:inline-block;padding:1px 7px;border-radius:9px;background:#37474f;color:#fff;font-size:11px;">'+esc(L.prefix)+'</span> '+esc(L.line)+' <span style="font-size:11px;color:#777;">('+esc(L.count)+')</span></summary><div style="padding:4px 10px 8px;font-size:11px;color:#333;">'+L.forms.map(esc).join(' · ')+'</div></details>'; });
      if(fs.other_total) h+='<div style="font-size:11px;color:#777;margin-top:3px;">+ '+esc(fs.other_total)+' state/carrier manuscript form(s) ('+(fs.other_prefixes||[]).map(esc).join(', ')+')</div>';
      h+='</div>';
    }
    // Public sources retrieved at query time (Q4) + snapshot for replay (Q5)
    const rt=data.retrieval;
    if(rt){
        // The panel and the PDF print the same named block — the spec's problem
      // #2 was the tool UI showing engineering ids too.
      h+='<details open style="margin:8px 0;background:#fff;border:1px solid #cfe9e4;border-radius:4px;"><summary style="cursor:pointer;padding:5px 8px;"><strong>Public sources retrieved</strong> <span style="font-size:11px;color:#777;">'+esc(rt.source_count)+' fetched · snapshot '+esc(rt.snapshot_id)+'</span></summary><div style="padding:6px 10px 10px;">';
      h+=window.spBuildPublicSourcesPrintHtml(rt, esc);
      h+='</div></details>';
    }
    // Derived stack (advisory / grounded in retrieval)
    const st=data.derived_stack;
    if(st){
      const badge = st.grounded
        ? '<span style="font-size:10px;color:#fff;background:#2e7d32;padding:1px 6px;border-radius:8px;">policy-grounded</span>'
        : '<span style="font-size:10px;color:#fff;background:#f9a825;padding:1px 6px;border-radius:8px;">advisory</span>';
      h+='<details style="margin:8px 0;background:#fff;border:1px solid #cfe9e4;border-radius:4px;"><summary style="cursor:pointer;padding:5px 8px;"><strong>Coverage stack — typical vs. this policy</strong> '+badge+'</summary>';
      h+='<div style="padding:6px 10px 10px;font-size:12px;">';
      if((st.typical_stack||[]).length) h+='<div><strong>Typical for this class:</strong><ul style="margin:4px 0 8px 18px;">'+st.typical_stack.map(x=>'<li>'+esc(x)+'</li>').join('')+'</ul></div>';
      if((st.present_in_policy||[]).length) h+='<div><strong style="color:#2e7d32;">✓ Present in this policy:</strong><ul style="margin:4px 0 8px 18px;">'+st.present_in_policy.map(x=>'<li>'+esc(x)+'</li>').join('')+'</ul></div>';
      if((st.likely_missing||[]).length) h+='<div><strong style="color:#c62828;">✗ Likely missing / gaps in this policy:</strong><ul style="margin:4px 0 8px 18px;">'+st.likely_missing.map(x=>'<li>'+esc(x)+'</li>').join('')+'</ul></div>';
      if((st.common_gaps||[]).length) h+='<div><strong>Commonly under-covered:</strong><ul style="margin:4px 0 0 18px;">'+st.common_gaps.map(x=>'<li>'+esc(x)+'</li>').join('')+'</ul></div>';
      if(st.notes) h+='<div style="font-size:11px;color:#555;margin-top:6px;">'+esc(st.notes)+'</div>';
      h+='<div style="font-size:10px;color:#999;margin-top:6px;">'+esc(st.note||'')+'</div></div></details>';
    }
    h+='</div>';
    ['fullReview','quickScreen'].forEach(id=>{
      const el=document.getElementById(id); if(!el || el.querySelector('#spNaicsPanel-'+id)) return;
      const block=h.replace('id="spNaicsPanel"','id="spNaicsPanel-'+id+'"');
      // Sits directly under the profile card inside Section 3; falls back to the
      // top of the tab on Quick Screen, which has no numbered sections.
      const prof=el.querySelector('[id^="spDetectedProfile"]');
      const host=el.querySelector('#spSection3Body');
      if(prof) prof.insertAdjacentHTML('afterend', block);
      else if(host) { host.querySelector('p') && (host.innerHTML=''); host.insertAdjacentHTML('beforeend', block); }
      else el.insertAdjacentHTML('afterbegin', block);
    });
  };

  // Print-optimized NAICS-Pivot block for the Compact/Print PDF. The live panel
  // uses <details> + flex that html2canvas drops; this renders as plain, fully
  // expanded tables/lists so everything (incl. NAICS) shows and is readable.
  // Demo toggle — reveals EB Section 10 (Retention Risk) & Section 11
  // (Recommended Enhancements) in the live Full Review, alongside the P&C.
  window.spToggleDemo = function(){
    // Not in a public trial: these are sections beyond the four it includes.
    if (typeof window.spIsTrialSession === 'function' && window.spIsTrialSession()) return;
    window._spDemo = !window._spDemo;
    const b=document.getElementById('spDemoBtn');
    if(b) b.textContent = window._spDemo ? '🎬 Demo: ON (EB 10 & 11)' : '🎬 Demo';
    document.querySelectorAll('[id^="spNaicsPanel-"]').forEach(el=>el.remove());
    if(window._naicsAnalysis){ try{ window.spRenderNaicsPanel(window._naicsAnalysis); }catch(e){ console.warn('demo re-render',e); } }
    else if(typeof showToast==='function'){ showToast('Run an analysis first, then click Demo to show EB Sections 10 & 11.', 'warning'); }
  };

  // ===== Public source naming ==============================================
  // Spec: "Final — Public Sources Component Spec" (v2, 2026-09-02). The block
  // printed engineering identifiers — FederalRegister:FERPA, BLS-QCEW:AZ — as
  // the primary label. A broker who sees "FERPA" knows what it is; one who
  // sees "FederalRegister:FERPA" thinks the report is broken. The audit trail
  // only works if the reader recognises the source, so the document title and
  // its publisher lead, and the identifier drops to muted metadata.
  //
  // The map holds the titles the spec fixes verbatim. Everything else is
  // DERIVED from the identifier's own shape — CFR citations, known acts, state
  // codes — so a source added to the library tomorrow is named without an edit
  // here. Only an id with no registry:citation shape at all falls back to
  // printing itself, because then there is nothing else to print.
  const SP_SOURCE_TITLES = {
    'FederalRegister:FERPA': ['Family Educational Rights and Privacy Act (FERPA)', 'U.S. Federal Register'],
    'eCFR:34CFR99': ['34 CFR Part 99 — FERPA Regulations', 'Electronic Code of Federal Regulations (eCFR)'],
    'FederalRegister:OSHA': ['Occupational Safety and Health Act (OSHA)', 'U.S. Federal Register'],
    'eCFR:29CFR1910': ['29 CFR Part 1910 — OSHA General Industry Standards', 'Electronic Code of Federal Regulations (eCFR)'],
    'BLS-QCEW:AZ': ['Quarterly Census of Employment and Wages — Arizona', 'U.S. Bureau of Labor Statistics'],
    'WebSearch(tavily):AZ': ['Arizona State Research — Supplemental Web Search', 'Tavily (search provider)']
  };
  const SP_SOURCE_PUBLISHERS = {
    'FederalRegister': 'U.S. Federal Register',
    'eCFR': 'Electronic Code of Federal Regulations (eCFR)',
    'BLS-QCEW': 'U.S. Bureau of Labor Statistics',
    'BLS': 'U.S. Bureau of Labor Statistics',
    'WebSearch(tavily)': 'Tavily (search provider)'
  };
  // Acts a citation may name. Titles the spec did not fix are still printed in
  // full rather than as an acronym the reader has to decode.
  const SP_ACT_TITLES = {
    FERPA: 'Family Educational Rights and Privacy Act (FERPA)',
    OSHA: 'Occupational Safety and Health Act (OSHA)',
    HIPAA: 'Health Insurance Portability and Accountability Act (HIPAA)',
    ERISA: 'Employee Retirement Income Security Act (ERISA)',
    ADA: 'Americans with Disabilities Act (ADA)',
    FLSA: 'Fair Labor Standards Act (FLSA)',
    COBRA: 'Consolidated Omnibus Budget Reconciliation Act (COBRA)',
    ACA: 'Affordable Care Act (ACA)',
    FMLA: 'Family and Medical Leave Act (FMLA)',
    GLBA: 'Gramm-Leach-Bliley Act (GLBA)',
    TCPA: 'Telephone Consumer Protection Act (TCPA)'
  };

  // registry:citation -> { title, publisher }. Never returns the raw id as the
  // title; an unrecognised registry still gets the citation spelled out.
  window.spSourceLabel = function(id){
    const raw = String(id == null ? '' : id);
    if (SP_SOURCE_TITLES[raw]) return { title: SP_SOURCE_TITLES[raw][0], publisher: SP_SOURCE_TITLES[raw][1], id: raw };
    const cut = raw.lastIndexOf(':');
    const registry = cut > 0 ? raw.slice(0, cut) : raw;
    const citation = cut > 0 ? raw.slice(cut + 1) : '';
    const publisher = SP_SOURCE_PUBLISHERS[registry] || registry || 'Public source';
    const stateName = (typeof STATE_PROFILES !== 'undefined' && STATE_PROFILES[citation] && STATE_PROFILES[citation].name) || null;
    let title;
    const cfr = /^(\d{1,2})CFR(\d{1,4})$/i.exec(citation);
    if (cfr) title = cfr[1] + ' CFR Part ' + cfr[2];
    else if (SP_ACT_TITLES[citation.toUpperCase()]) title = SP_ACT_TITLES[citation.toUpperCase()];
    else if (/^WebSearch/i.test(registry) && stateName) title = stateName + ' State Research — Supplemental Web Search';
    else if (/QCEW/i.test(registry) && stateName) title = 'Quarterly Census of Employment and Wages — ' + stateName;
    else if (stateName) title = stateName + ' — ' + publisher;
    else if (citation) title = citation;
    else title = publisher;
    return { title: title, publisher: publisher, id: raw };
  };

  // ===== "Public sources retrieved" — print/PDF rendering =====
  // Spec: "SentriPol PDF Report — Public Sources Component Spec" (2026-09-02).
  //
  // The PDF used to collapse this to a bare "Compliance Record — Public
  // Sources" list of names, dropping the link, the HTTP status and the fetch
  // time. That block IS the defensibility feature: it is what lets a reviewer
  // verify that a regulatory finding was fetched, hashed and can be replayed,
  // instead of taking the report's word for it. So the print build now emits
  // every row expanded, with the same six elements the tool UI shows —
  // status dot, namespaced source id, `source` link, HTTP status, content
  // hash, and the fetch timestamp on a muted second line — plus the replay
  // footer.
  //
  // `esc` is passed in rather than closed over: the print builders each carry
  // their own DOM-based escaper, and the headless test harness has no DOM (a
  // page-local `escapeHtml` returns '' there), so the caller supplies one.
  window.spBuildPublicSourcesPrintHtml = function(rt, esc){
    const R = rt || {}, sources = R.sources || [];
    if(!sources.length) return '';
    const MONO = 'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace';
    // Only http(s) becomes a live link — `esc` neutralises markup but not a
    // `javascript:` scheme, and this URL comes back from the retrieval layer.
    const linkable = u => /^https?:\/\//i.test(String(u||''));
    const dot = color => '<span style="display:inline-block;width:7px;height:7px;border-radius:50%;'
      + 'background:' + color + ';margin-right:6px;vertical-align:middle;"></span>';
    const count = (R.source_count == null ? sources.length : R.source_count);
    let h = '<div style="font-weight:700;color:#003366;margin:10px 0 4px;font-size:12px;">Public sources retrieved '
      + '<span style="font-weight:400;color:#777;font-size:11px;">— ' + esc(count) + ' fetched · snapshot '
      + esc(R.snapshot_id || '—') + '</span></div>';
    sources.forEach(s => {
      const code = Number(s.http_status);
      const ok = code >= 200 && code < 300;
      // Spec defines green for 200 and red for any non-2xx. A source still
      // pending (the Tavily POST path, which is captured but not GET
      // re-verifiable) has no status at all and would read as a failure in
      // red — it gets amber, and its reason prints with the audit metadata.
      const color = ok ? '#2e7d32' : (s.pending ? '#f9a825' : '#c62828');
      const L = window.spSourceLabel(s.source);
      // Line 1: the document, named. Line 2: publisher + link. Line 3: the
      // audit trail, muted — for the auditor, not the broker.
      h += '<div style="margin:5px 0;font-size:11px;page-break-inside:avoid;">'
        + dot(color) + '<strong>' + esc(L.title) + '</strong>'
        + '<div style="margin-left:13px;font-style:italic;color:#444;">' + esc(L.publisher)
        + (linkable(s.url) ? ' · <a href="' + esc(s.url) + '" style="color:#00695c;font-style:normal;">source</a>' : '')
        + '</div>'
        + '<div style="margin-left:13px;font-family:' + MONO + ';font-size:9px;color:#999;">'
        + (s.http_status ? 'HTTP ' + esc(s.http_status) : (s.pending ? esc(s.pending) : 'no status'))
        + (s.content_hash ? ' · #' + esc(s.content_hash) : '')
        + ' · fetched ' + esc(s.fetched_at || '—')
        + ' · id: ' + esc(L.id) + '</div>'
        + '</div>';
    });
    h += '<div style="font-size:9px;color:#999;font-style:italic;margin-top:5px;">'
      + 'Snapshotted for byte-identical replay — GET /api/analyze/vertical/snapshot/'
      + esc(R.snapshot_id || '') + ' (append /verify for drift).</div>';
    return h;
  };

  // ===== Case-law exclusions — same grammar as the sources block ===========
  // A form number is an engineering identifier in exactly the way
  // "FederalRegister:FERPA" is: `CG 21 47` tells a broker nothing until it is
  // named. So the exclusion's plain-English name leads, the form number joins
  // the muted audit line with the matched text, and the case-law note sits
  // under it as the reason the flag exists.
  window.spBuildExclusionsHtml = function(flags, esc, opts){
    const list = flags || [], o = opts || {};
    const MONO = 'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace';
    if(!list.length) return o.emptyHtml == null ? '<span style="color:#777;">none detected</span>' : o.emptyHtml;
    const dot = '<span style="display:inline-block;width:7px;height:7px;border-radius:50%;'
      + 'background:#c62828;margin-right:6px;vertical-align:middle;"></span>';
    return list.map(e =>
      '<div style="margin:5px 0;font-size:' + (o.print ? '11px' : '12px') + ';page-break-inside:avoid;'
      + (o.print ? '' : 'border-left:3px solid #c62828;padding:4px 8px;background:#fff;') + '">'
      + dot + '<strong>' + esc(e.name || e.form) + '</strong>'
      + (e.caselaw ? '<div style="margin-left:13px;font-style:italic;color:#444;">' + esc(e.caselaw) + '</div>' : '')
      + '<div style="margin-left:13px;font-family:' + MONO + ';font-size:9px;color:#999;">form ' + esc(e.form)
      + (e.matched ? ' · matched "' + esc(e.matched) + '"' : '') + '</div>'
      + '</div>'
    ).join('');
  };

  window.buildNaicsPrintHtml = function(demoMode){
    const d = window._naicsAnalysis, prof = window._detectedProfile;
    if(!d) return '';
    const esc=s=>{const x=document.createElement('div');x.textContent=String(s==null?'':s);return x.innerHTML;};
    const n=d.naics||{}, snap=d.compliance_snapshot||{}, rt=d.retrieval||{}, st=d.derived_stack||{};
    const row=(k,v)=>'<tr><td style="padding:3px 6px;width:150px;color:#555;vertical-align:top;">'+k+'</td><td style="padding:3px 6px;">'+v+'</td></tr>';
    let h='<div style="border:1px solid #00695c;border-radius:6px;padding:12px 14px;margin:14px 0;background:#f3fbf9;page-break-inside:avoid;">';
    h+='<div style="font-size:15px;font-weight:700;color:#00695c;margin-bottom:8px;">NAICS Vertical Analysis</div>';
    h+='<table style="width:100%;border-collapse:collapse;font-size:11px;">';
    h+=row('NAICS classification','<b>'+esc(n.code||'—')+'</b> — '+esc(n.vertical||'—')+' <span style="color:#777;">('+esc(n.source)+')</span>');
    h+=row('NAICS roll-up', esc((n.rollup||[]).join('  →  ')));
    if(prof){ const _vf=v=>v.name+(v.site==='confirmed'?' (confirmed by company site)':v.site==='absent'?' (NOT on company site — policy paper only)':v.site==='no-site'?' (no site info supplied)':'');
              // Same rule as the panel: a vertical the company's own site does
              // not describe is not printed as a detected vertical.
              const _vs = window.spVerticalsForDisplay(prof.verticals);
              h+=row('Detected verticals', esc(_vs.shown.map(_vf).join(', ')
                     || (_vs.hidden.length ? 'none corroborated by the company website' : '—')));
              if(_vs.hidden.length) h+=row('<span style="color:#8a6d00;">Not corroborated by the site</span>',
                     '<span style="color:#8a6d00;">'+esc(_vs.hidden.map(v=>v.name).join(', '))+' — from policy paper only</span>');
              h+=row('Operating states', esc((prof.states||[]).map(s=>s.name+' ('+s.abbr+')').join(', ')||'—')); }
    if((d.carriers||[]).length){ h+=row('Carrier / program <span style="color:#777;font-weight:400;">(insurer, not the insured)</span>', esc(d.carriers.map(c=>c.name).join('; '))); }
    h+='</table>';
    const sec=t=>'<div style="font-weight:700;color:#003366;margin:10px 0 3px;font-size:12px;">'+t+'</div>';
    h+=sec('Applicable Federal Frameworks (by NAICS)');
    h+=(d.federal_frameworks||[]).length ? '<ul style="margin:2px 0 0 18px;">'+d.federal_frameworks.map(f=>'<li><b>'+esc(f.framework)+'</b> — '+esc(f.rule)+' <i style="color:#777;">('+esc(f.citation)+')</i></li>').join('')+'</ul>' : '<div style="color:#777;">None mapped.</div>';
    const lob=_spLob();
    const lobLabel = lob==='pc' ? 'P&C' : (lob==='eb' ? 'Employee Benefits' : 'all lines');
    h+=sec('State Mandate Obligations ('+lobLabel+')');
    const sm=_spFilterMandates(d.state_mandates||{}, lob);
    if(Object.keys(sm).length){ Object.keys(sm).forEach(k=>{ h+='<div style="margin:2px 0;"><b>'+esc(k)+':</b> '+sm[k].map(m=>esc(m.mandate)+' <span style="color:#777;">('+esc(m.citation)+')</span>').join(' · ')+'</div>'; }); }
    else { h+='<div style="color:#777;">None for the '+esc(lobLabel)+' line in these states.</div>'; }
    // Demo-only EB Section 10 (Employee Retention Risk) + Section 11 (Recommended
    // Enhancements with Cost per Employee), alongside the P&C content.
    if(demoMode && d.eb_preview){
      const ep=d.eb_preview; const dm='<span style="font-size:9px;color:#fff;background:#f9a825;padding:1px 5px;border-radius:7px;font-weight:600;">DEMO · EB</span>';
      h+=sec('10. '+esc(ep.section9_title||'Employee Retention Risk Assessment')+' '+dm);
      (ep.section9||[]).forEach(f=>{ h+='<div style="margin:2px 0;">• <b>'+esc(f.factor)+'</b> ['+esc(f.risk)+'] — benchmark '+esc(f.benchmark)+' <span style="color:#777;font-size:10px;">'+esc(f.note)+'</span></div>'; });
      h+=sec('11. '+esc(ep.section10_title||'Recommended Enhancements with Cost per Employee')+' '+dm);
      h+='<table style="width:100%;border-collapse:collapse;font-size:11px;margin:2px 0;">'+(ep.section10||[]).map(x=>'<tr><td style="padding:2px 6px;">'+esc(x.enhancement)+'</td><td style="padding:2px 6px;text-align:right;font-weight:600;">'+esc(x.cost_per_employee)+'</td></tr>').join('')+'</table>';
      if(ep.note) h+='<div style="font-size:9px;color:#999;margin-top:2px;">'+esc(ep.note)+'</div>';
    }
    h+=sec('Coverage Stack — This Policy (grounded)');
    if((st.present_in_policy||[]).length) h+='<div style="margin:2px 0;"><b style="color:#1b5e20;">Present:</b> '+st.present_in_policy.map(esc).join('; ')+'</div>';
    if((st.likely_missing||[]).length) h+='<div style="margin:2px 0;"><b style="color:#b71c1c;">Likely missing / gaps:</b> '+st.likely_missing.map(esc).join('; ')+'</div>';
    if(!(st.present_in_policy||[]).length && !(st.likely_missing||[]).length && (st.typical_stack||[]).length) h+='<div>Typical: '+st.typical_stack.map(esc).join('; ')+'</div>';
    if((d.exclusion_flags||[]).length){ h+=sec('Case-law exclusions flagged present in this policy'); h+=window.spBuildExclusionsHtml(d.exclusion_flags, esc, { print: true }); }
    if(d.forms_schedule && (d.forms_schedule.lines||[]).length){
      const fs=d.forms_schedule;
      h+=sec('Forms Schedule (ISO/bureau — '+esc(fs.total_forms)+' forms)');
      fs.lines.forEach(L=>{ h+='<div style="margin:2px 0;"><b>'+esc(L.prefix)+' — '+esc(L.line)+'</b> ('+esc(L.count)+'): <span style="font-size:10px;color:#555;">'+L.forms.map(esc).join(', ')+'</span></div>'; });
      if(fs.other_total) h+='<div style="font-size:10px;color:#777;margin-top:2px;">+ '+esc(fs.other_total)+' state/carrier manuscript form(s): '+(fs.other_prefixes||[]).map(esc).join(', ')+'</div>';
    }
    h+=window.spBuildPublicSourcesPrintHtml(rt, esc);
    h+='<div style="font-size:9px;color:#999;margin-top:8px;">SentriPol™ · DAF NAICS vertical analysis · reference library v'+esc(snap.library_version)+' · analyzed '+esc((snap.analyzed_at||'').slice(0,19))+' UTC</div>';
    h+='</div>';
    return h;
  };

  // ===== BOP Review (BOP Review Design Spec v1.0 — rules R1–R4) =====
  // Ten sections in spec order, each fed by the component stack rather than a
  // linear pipeline off the PDF. Section 5 (NOT COVERED) carries the most
  // visual weight and is pointed to from Section 1, per the spec's rendering
  // priority: a broker must not have to scroll past the CORE analysis to reach
  // the missing-policy findings.
  window.spBopAnalyze = async function(sources, states, opts){
    const o = opts || {};
    const ordered=[...sources].sort((a,b)=>(b.weight||1)-(a.weight||1));
    // Much larger budget than the LLM-bound calls above: the BOP sections are
    // deterministic scans (forms schedules, endorsement numbers, limit tables)
    // that live deep in a 150-200pp policy. At the 55k cap used elsewhere the
    // Hartford test policy lost its cyber-exposure evidence and the gap went
    // unreported. The backend bounds its own LLM context separately.
    let text=''; for(const s of ordered){ if(text.length>400000) break; text+='\n['+s.label+']\n'+(s.text||'').slice(0,200000); }
    const token=await _login();
    const r=await _fetchT(DAF_BASE+'/api/analyze/bop',{method:'POST',headers:{...(token ? {'Authorization':'Bearer '+token} : {}),'Content-Type':'application/json'},
      body:JSON.stringify({ text, states:states||[], naics:o.naics||null, present_coverages:o.presentCoverages||[],
                            business_context:o.businessContext||'', policyholder:o.policyholder||null, enrich:true })});
    if(!r.ok) throw new Error('bop '+r.status);
    return await r.json();
  };

  const SP_CRIT_COLORS = { CRITICAL:'#b71c1c', HIGH:'#e65100', MEDIUM:'#f9a825', LOW:'#0277bd', OK:'#2e7d32', INFO:'#616161' };

  window.spRenderBopPanel = function(data){
    if(!data || !data.detection || !data.detection.is_bop) return;
    const esc=s=>{const d=document.createElement('div');d.textContent=String(s==null?'':s);return d.innerHTML;};
    const S=data.sections||{}, h1=S.s1_header||{}, rec=data.compliance_record||{};
    const flag=c=>'<span style="display:inline-block;padding:1px 7px;border-radius:9px;background:'+(SP_CRIT_COLORS[c]||'#616161')+';color:#fff;font-size:10px;font-weight:700;">'+esc(c)+'</span>';
    const sec=(n,t,fed)=>'<div style="font-weight:700;color:#003366;margin:14px 0 4px;font-size:13px;">Section '+n+' — '+esc(t)
      +'<div style="font-weight:400;font-size:10px;color:#888;">Fed by: '+esc(fed)+'</div></div>';
    const money=v=>v==null?'—':esc(v);
    const row=(k,v)=>'<tr><td style="padding:2px 6px;color:#555;width:170px;vertical-align:top;">'+k+'</td><td style="padding:2px 6px;">'+v+'</td></tr>';

    let h='<div id="spBopPanel" style="border:2px solid #003366;border-radius:8px;padding:12px 14px;margin:0 0 16px;background:#f6f9fc;">';
    h+='<div style="font-weight:700;color:#003366;font-size:15px;">🏢 BOP Review <span style="font-weight:400;font-size:11px;color:#777;">— BOP Review Design Spec v1.0 · rules R1–R4 · lib v'+esc(rec.bop_library_version)+'</span></div>';

    // ---- Section 1: Policy Header (R1) ----
    h+=sec(1,'Policy Header [R1]','Policy ingestion · Website scrape · Auto-fetch · NAICS classification');
    h+='<div style="background:#003366;color:#fff;padding:6px 10px;border-radius:5px;font-weight:700;font-size:13px;">'+esc(h1.policy_type_badge)+'</div>';
    h+='<table style="width:100%;border-collapse:collapse;font-size:12px;margin-top:6px;">';
    h+=row('Carrier / program', esc(h1.carrier||'—')+(h1.carrier_program?' <span style="color:#777;">('+esc(h1.carrier_program)+')</span>':''));
    h+=row('Policy number', esc(h1.policy_number||'—'));
    h+=row('Insured', esc(h1.insured||'—'));
    const nn=data.naics||{};
    const cheat=nn.cheatsheet||{};
    h+=row('NAICS classification', '<b>'+esc(nn.code||'—')+'</b> — '+esc(cheat.industry||nn.vertical||'—')
      +' <span style="color:#777;">('+esc(nn.source||'—')
      +(nn.class_code?' · GL class '+esc(nn.class_code)+' "'+esc(nn.class_description)+'"':'')+')</span>'
      +(cheat.vintage_note?'<div style="font-size:10px;color:#a67c00;">'+esc(cheat.vintage_note)+'</div>':''));
    h+=row('Locations', esc((h1.locations||[]).join(' · ')||'—')+(h1.square_footage?' <span style="color:#777;">('+esc(h1.square_footage)+' sq ft)</span>':''));
    if((h1.classification_schedule||[]).length){
      h+=row('GL classification', h1.classification_schedule.map(c=>'<b>'+esc(c.class_code)+'</b> '+esc(c.description)
        +' <span style="color:#777;">('+esc(c.exposure)+' '+esc(c.basis)+')</span>').join('<br>'));
    }
    h+=row('Premium / term', esc(h1.annual_premium||'—')+(h1.policy_term?' · '+esc(h1.policy_term.effective)+' → '+esc(h1.policy_term.expiration):''));
    h+='</table>';
    h+='<div style="font-size:11px;font-style:italic;color:#333;background:#fff;border-left:3px solid #003366;padding:6px 9px;margin:6px 0;">'+esc(h1.bop_definition)+'</div>';

    // ---- Section 2: CORE (R2) ----
    const c2=S.s2_core||{}, p=c2.property||{}, gl=c2.general_liability||{}, bi=c2.business_income||{};
    const val=x=>(x&&x.value)?esc(x.value):'—';
    h+=sec(2,'BOP CORE Coverage Analysis [R2]','Policy ingestion · ISO forms library · Peer benchmark database');
    h+='<table style="width:100%;border-collapse:collapse;font-size:12px;">';
    h+=row('<b>2A. Commercial Property</b>','Building '+val(p.building)+' · BPP '+val(p.business_personal_property)
      +' · Deductible '+val(p.deductible)+'<br><span style="color:#777;font-size:11px;">'+esc(p.valuation||'valuation n/f')+' · '+esc(p.perils||'perils n/f')+' · '+esc(p.coinsurance||'coinsurance n/f')+'</span>');
    h+=row('<b>2B. General Liability</b>','Each occurrence '+val(gl.each_occurrence)+' · Aggregate '+val(gl.general_aggregate)
      +' · Products/CO '+val(gl.products_completed_ops)+'<br><span style="color:#777;font-size:11px;">Pers &amp; Adv '+val(gl.personal_advertising_injury)
      +' · Med pay '+val(gl.medical_payments)+' · Premises rented '+val(gl.damage_to_premises_rented)+' · Base form '+esc(gl.base_form||'n/f')+'</span>');
    h+=row('<b>2C. Business Income</b>', esc(bi.basis||'basis n/f')+' · '+(bi.period_months?esc(bi.period_months)+' months':'period n/f')
      +' · '+(bi.waiting_period_hours?esc(bi.waiting_period_hours)+'-hour wait':'waiting period n/f')
      +'<br><span style="color:#777;font-size:11px;">Dependent property '+(bi.dependent_property?'✓':'—')+' · Extended indemnity '+(bi.extended_period_of_indemnity?'✓':'—')+'</span>');
    h+='</table>';
    h+='<div style="font-size:10px;color:#999;">'+esc(c2.note||'')+'</div>';

    // ---- Section 3: Elected add-ons (R3) ----
    h+=sec(3,'Elected Add-Ons [R3]','Carrier endorsement library · ISO forms library');
    const el=S.s3_addons_elected||[];
    if(el.length){
      h+='<table style="width:100%;border-collapse:collapse;font-size:12px;background:#fff;">';
      h+='<tr style="background:#e8eef5;"><th style="text-align:left;padding:3px 6px;">Add-On</th><th style="text-align:left;padding:3px 6px;">Form(s)</th><th style="text-align:left;padding:3px 6px;">Limit</th><th style="text-align:left;padding:3px 6px;">Finding</th></tr>';
      el.forEach(a=>{ h+='<tr><td style="padding:3px 6px;border-top:1px solid #eee;">'+esc(a.name)+'</td>'
        +'<td style="padding:3px 6px;border-top:1px solid #eee;font-size:11px;">'+esc((a.forms||[]).join(', ')||'—')+(a.carrier?' <span style="color:#777;">('+esc(a.carrier)+')</span>':'')+'</td>'
        +'<td style="padding:3px 6px;border-top:1px solid #eee;">'
          +(a.limit ? money(a.limit) : '<span style="color:#777;font-style:italic;font-size:11px;">Not shown on dec</span>')+'</td>'
        +'<td style="padding:3px 6px;border-top:1px solid #eee;font-size:11px;">'+esc(a.finding)
          // Cite where the form was found so a broker can verify it in the policy.
          +(a.evidence?'<div style="font-size:10px;color:#999;margin-top:2px;">cited: "'+esc(a.evidence)+'"</div>':'')
        +'</td></tr>'; });
      h+='</table>';
    } else { h+='<div style="color:#777;font-size:12px;">No add-on endorsements confirmed on the declarations.</div>'; }
    const unc=S.s3_addons_unconfirmed||[];
    if(unc.length){ h+='<div style="font-size:11px;color:#777;margin-top:4px;">Named in coverage-form text only (confirm against the dec): '+unc.map(a=>esc(a.name)).join(' · ')+'</div>'; }

    // ---- Section 4: Available add-ons NOT elected (R3) ----
    h+=sec(4,'Available Add-Ons NOT Elected [R3]','Carrier endorsement library · NAICS classification · Website scrape');
    const ne=S.s4_addons_not_elected||[];
    if(ne.length){
      h+='<table style="width:100%;border-collapse:collapse;font-size:12px;background:#fff;">';
      h+='<tr style="background:#e8eef5;"><th style="text-align:left;padding:3px 6px;">Add-On</th><th style="text-align:left;padding:3px 6px;">Why It Matters</th><th style="padding:3px 6px;">Criticality</th><th style="text-align:left;padding:3px 6px;">Action</th></tr>';
      ne.forEach(a=>{ h+='<tr><td style="padding:3px 6px;border-top:1px solid #eee;">'+esc(a.addon)+(a.declined_on_schedule?' <span style="font-size:10px;color:#b71c1c;">(offered &amp; declined)</span>':'')+'</td>'
        +'<td style="padding:3px 6px;border-top:1px solid #eee;font-size:11px;">'+esc(a.why)+'</td>'
        +'<td style="padding:3px 6px;border-top:1px solid #eee;text-align:center;">'+flag(a.criticality)+'</td>'
        +'<td style="padding:3px 6px;border-top:1px solid #eee;font-size:11px;">'+esc(a.action)+'</td></tr>'; });
      h+='</table>';
    } else { h+='<div style="color:#777;font-size:12px;">No further add-ons flagged for this class.</div>'; }

    // ---- Section 5: NOT COVERED (R4) — the key section ----
    const nc=S.s5_not_covered||[];
    h+='<div style="border:3px solid #b71c1c;border-radius:8px;margin:14px 0;background:#fff;">';
    h+='<div style="background:#b71c1c;color:#fff;padding:8px 12px;font-weight:700;font-size:14px;">NOT COVERED · Standalone Policies Recommended [R4]</div>';
    h+='<div style="padding:8px 12px;font-size:11px;color:#555;border-bottom:1px solid #eee;">This is a Business Owner\'s Policy. BOPs by design cannot include the following coverages — each requires a separate standalone policy'
      + (nn.code?' . For an organization classified under NAICS '+esc(nn.code)+' ('+esc(nn.vertical||'—')+')':'')
      + ((data._states&&data._states.length)?' in '+esc(data._states.join(', ')):'')+':</div>';
    if(nc.length){
      nc.forEach(r=>{
        const isGap=r.criticality!=='INFO';
        h+='<div style="padding:8px 12px;border-bottom:1px solid #f0f0f0;'+(isGap?'':'opacity:.7;')+'">';
        h+='<div style="font-weight:700;font-size:12px;">'+flag(r.criticality)+' '+esc(r.coverage)+'</div>';
        h+='<div style="font-size:11px;margin-top:3px;"><strong>Rationale:</strong> '+esc(r.rationale)+'</div>';
        h+='<div style="font-size:11px;"><strong>Recommendation:</strong> '+esc(r.recommendation)+'</div>';
        if(r.regulatory_driver) h+='<div style="font-size:11px;color:#00695c;"><strong>Regulatory driver:</strong> '+esc(r.regulatory_driver)+'</div>';
        if(r.broker_note) h+='<div style="font-size:11px;color:#6d4c00;background:#fff8e1;border-left:2px solid #f9a825;padding:3px 7px;margin-top:3px;">📌 '+esc(r.broker_note)+'</div>';
        h+='<div style="font-size:10px;color:#999;margin-top:2px;">Trigger: '+esc(r.trigger)+' · '+esc(r.status)+(r.rationale_source==='llm-tailored'?' · rationale tailored to this insured':'')+'</div>';
        h+='</div>';
      });
    } else { h+='<div style="padding:8px 12px;color:#777;font-size:12px;">No standalone-policy gaps triggered for this NAICS/state combination.</div>'; }
    h+='</div>';

    // ---- Section 6: Restriction endorsements ----
    h+=sec(6,'Coverage Restriction Endorsements Detected','ISO forms library · Carrier endorsement library');
    const rs=(S.s6_restrictions||[]).filter(r=>!r.is_addon), ra=(S.s6_restrictions||[]).filter(r=>r.is_addon);
    if(rs.length){
      h+='<table style="width:100%;border-collapse:collapse;font-size:12px;background:#fff;">';
      h+='<tr style="background:#e8eef5;"><th style="text-align:left;padding:3px 6px;">Form</th><th style="text-align:left;padding:3px 6px;">Endorsement</th><th style="text-align:left;padding:3px 6px;">What It Removes</th><th style="text-align:left;padding:3px 6px;">Exposure Impact</th></tr>';
      rs.forEach(r=>{ h+='<tr><td style="padding:3px 6px;border-top:1px solid #eee;font-weight:600;">'+esc(r.form)+'</td>'
        +'<td style="padding:3px 6px;border-top:1px solid #eee;font-size:11px;">'+esc(r.name)+'</td>'
        +'<td style="padding:3px 6px;border-top:1px solid #eee;font-size:11px;">'+esc(r.restricts)+'</td>'
        +'<td style="padding:3px 6px;border-top:1px solid #eee;font-size:11px;color:#b71c1c;">'+esc(r.impact)+'</td></tr>'; });
      h+='</table>';
    } else { h+='<div style="color:#777;font-size:12px;">No restriction endorsements from the library detected on this policy.</div>'; }
    const rmeta=S.s6_restrictions_meta||{};
    if(rmeta.library_gap && rmeta.note){
      h+='<div style="margin-top:6px;padding:6px 9px;background:#fff8e1;border-left:3px solid #f9a825;font-size:11px;color:#6d4c00;">⚠️ '+esc(rmeta.note)+'</div>';
    }
    if(ra.length) h+='<div style="font-size:11px;color:#2e7d32;margin-top:4px;">Coverage-BROADENING forms also present: '+ra.map(r=>esc(r.form)+' '+esc(r.name)).join(' · ')+'</div>';

    // ---- Section 7: Peer benchmarks ----
    const bm=S.s7_benchmarks||{};
    h+=sec(7,'Peer Benchmark Comparison','NAICS classification · Peer benchmark database · Auto-fetch');
    // R2 report output: each BOP CORE part carries its own MEETS / BELOW
    // STANDARD / HIGH GAP indicator, which is what the broker reads first.
    if((bm.core_parts||[]).length){
      const pc=st=>st==='HIGH GAP'?'#b71c1c':(st==='BELOW STANDARD'?'#e65100':((st==='NOT EVALUATED'||st==='PARTIAL')?'#757575':'#2e7d32'));
      h+='<table style="width:100%;border-collapse:collapse;font-size:12px;background:#fff;margin-bottom:6px;">';
      h+='<tr style="background:#e8eef5;"><th style="text-align:left;padding:3px 6px;">BOP CORE part</th><th style="text-align:left;padding:3px 6px;">This policy</th><th style="padding:3px 6px;">Indicator</th></tr>';
      bm.core_parts.forEach(p=>{ h+='<tr><td style="padding:3px 6px;border-top:1px solid #eee;font-weight:600;">'+esc(p.part)+'</td>'
        +'<td style="padding:3px 6px;border-top:1px solid #eee;font-size:11px;">'+esc(p.detail)+'</td>'
        +'<td style="padding:3px 6px;border-top:1px solid #eee;text-align:center;font-weight:700;font-size:11px;color:'+pc(p.status)+';">'+esc(p.status)+'</td></tr>'; });
      h+='</table>';
    }
    h+='<table style="width:100%;border-collapse:collapse;font-size:12px;background:#fff;">';
    h+='<tr style="background:#e8eef5;"><th style="text-align:left;padding:3px 6px;">Coverage</th><th style="padding:3px 6px;">This Policy</th><th style="padding:3px 6px;">Peer Median</th><th style="padding:3px 6px;">Peer 75th</th><th style="padding:3px 6px;">Status</th></tr>';
    (bm.rows||[]).forEach(r=>{ const bad=r.status==='BELOW STANDARD';
      h+='<tr><td style="padding:3px 6px;border-top:1px solid #eee;">'+esc(r.coverage)+'</td>'
        +'<td style="padding:3px 6px;border-top:1px solid #eee;text-align:center;font-weight:600;">'+esc(r.this_policy)+'</td>'
        +'<td style="padding:3px 6px;border-top:1px solid #eee;text-align:center;">'+esc(r.peer_median)+'</td>'
        +'<td style="padding:3px 6px;border-top:1px solid #eee;text-align:center;">'+esc(r.peer_p75)+'</td>'
        +'<td style="padding:3px 6px;border-top:1px solid #eee;text-align:center;color:'+(bad?'#b71c1c':'#2e7d32')+';font-weight:700;font-size:11px;">'+esc(r.status)+'</td></tr>'; });
    h+='</table>';
    h+='<div style="font-size:10px;color:#999;margin-top:3px;">Peer set: '+esc(bm.peer_set||'')
      +(bm.square_footage_basis?' · BPP bracket from '+esc(bm.square_footage_basis):'')+'. '+esc(bm.provenance||'')+'</div>';

    // ---- Section 8: Regulatory compliance ----
    const rg=S.s8_regulatory||{};
    h+=sec(8,'Regulatory Compliance Context','State regulatory library · Federal regulatory library · NAICS classification');
    const sm=rg.state||{};
    if(Object.keys(sm).length){ Object.keys(sm).forEach(st=>{ h+='<div style="font-size:11px;margin:2px 0;"><b>'+esc(st)+':</b> '
      +sm[st].map(x=>esc(x.mandate)+' — '+esc(x.trigger)+' <i style="color:#777;">('+esc(x.citation)+')</i>').join('<br>&nbsp;&nbsp;')+'</div>'; }); }
    else { h+='<div style="color:#777;font-size:12px;">No state mandates mapped for the detected states.</div>'; }
    if((rg.federal||[]).length){ h+='<div style="font-size:11px;margin-top:4px;"><b>Federal (NAICS '+esc(nn.code||'—')+'):</b><br>'
      +rg.federal.map(f=>'&nbsp;&nbsp;'+esc(f.framework)+' — '+esc(f.rule)+' <i style="color:#777;">('+esc(f.citation)+')</i>').join('<br>')+'</div>'; }
    if((rg.federal_industry||[]).length){ h+='<div style="font-size:11px;margin-top:4px;"><b>Industry-specific federal context:</b><br>'
      +rg.federal_industry.map(f=>'&nbsp;&nbsp;'+esc(f.rule)+' <i style="color:#777;">('+esc(f.citation)+')</i>').join('<br>')+'</div>'; }
    if((rg.broker_reminders||[]).length){
      h+='<details style="margin-top:6px;font-size:11px;"><summary style="cursor:pointer;color:#6d4c00;">Broker reminders (NAICS cheat sheet)</summary><div style="padding:4px 8px;">'
        +rg.broker_reminders.map(r=>'• '+esc(r.note)).join('<br>')+'</div></details>';
    }

    // ---- Section 9: Findings summary ----
    h+=sec(9,'Findings Summary','All prior sections aggregated');
    const fnd=S.s9_findings||[];
    if(fnd.length){ h+='<div style="background:#fff;border:1px solid #e0e0e0;border-radius:4px;padding:4px 8px;">';
      fnd.forEach(f=>{ h+='<div style="font-size:11px;padding:3px 0;border-bottom:1px solid #f5f5f5;">'+flag(f.criticality)+' '+esc(f.finding)
        +' <span style="color:#999;">→ '+esc(f.section)+'</span><br><span style="color:#555;margin-left:4px;">↳ '+esc(f.action)+'</span></div>'; });
      h+='</div>'; }
    else { h+='<div style="color:#777;font-size:12px;">No findings.</div>'; }

    // ---- Section 10: Recommended actions ----
    h+=sec(10,'Recommended Actions','Findings Summary');
    h+='<ol style="margin:2px 0 0 18px;font-size:12px;">'+(S.s10_actions||[]).map(a=>'<li style="margin:2px 0;">'+esc(a.action)+' — '+flag(a.criticality)+'</li>').join('')+'</ol>';

    // ---- Deep Policy Text Scan ----
    // Same section, same order, as the P&C and EB reports carry. The BOP
    // review reads the same uploaded documents, so coverage written into
    // another form — or the explicit absence of it — belongs on this record.
    {
      const _fd = window._lastFormData;
      if (_fd && _fd.deepScanResults) {
        h+=sec('DS','Deep Policy Text Scan — Embedded Coverage Detection','Uploaded policy documents');
        h+=window.spDeepScanSectionHtml(_fd, esc);
      }
    }

    // ---- Compliance Record footer ----
    const src=rec.sources_referenced||{};
    h+='<div style="margin-top:12px;padding-top:8px;border-top:1px solid #ccd;font-size:10px;color:#777;">'
      +'<b>Compliance Record</b><br>Analysis timestamp: '+esc((rec.analysis_timestamp||'').slice(0,19))+' UTC · '+esc(rec.analysis_version||'')
      +'<br>Sources referenced: '+esc(src.iso_forms||0)+' ISO forms, '+esc(src.carrier_endorsements||0)+' carrier endorsements, '
      +esc(src.state_regulatory_citations||0)+' state citations, '+esc(src.federal_regulatory_citations||0)+' federal citations, '
      +esc(src.peer_benchmark_records||0)+' peer benchmark records'
      +'<br>Compliance Record ID: '+esc(rec.compliance_record_id||'')
      +' — replay: <code>GET /api/analyze/bop/record/'+esc(rec.compliance_record_id||'')+'</code></div>';
    h+='</div>';

    ['fullReview','quickScreen'].forEach(id=>{
      const el=document.getElementById(id); if(!el || el.querySelector('#spBopPanel-'+id)) return;
      const block=h.replace('id="spBopPanel"','id="spBopPanel-'+id+'"');
      // Leads the whole review, above Section 1: for a BOP the missing-policy
      // findings are the headline. It carries its own Section 1-10 numbering, so
      // it deliberately stays outside the report's sequence rather than being
      // folded into it (the profile/NAICS panels moved down into Section 3).
      el.insertAdjacentHTML('afterbegin', block);
    });
  };

  // Print-optimized BOP block for the Compact/Print PDF — plain tables only, so
  // html2canvas keeps everything (the live panel's nested flex/detail markup
  // rasterises poorly).
  window.buildBopPrintHtml = function(){
    const d=window._bopAnalysis;
    if(!d || !d.detection || !d.detection.is_bop) return '';
    const esc=s=>{const x=document.createElement('div');x.textContent=String(s==null?'':s);return x.innerHTML;};
    const S=d.sections||{}, h1=S.s1_header||{}, rec=d.compliance_record||{}, nn=d.naics||{};
    const sec=t=>'<div style="font-weight:700;color:#003366;margin:9px 0 3px;font-size:12px;">'+t+'</div>';
    let h='<div style="border:1px solid #003366;border-radius:6px;padding:12px 14px;margin:14px 0;background:#f6f9fc;page-break-inside:avoid;">';
    h+='<div style="font-size:15px;font-weight:700;color:#003366;">BOP Review</div>';
    h+='<div style="background:#003366;color:#fff;padding:5px 9px;border-radius:4px;font-weight:700;font-size:12px;margin:6px 0;">'+esc(h1.policy_type_badge)+'</div>';
    h+='<table style="width:100%;border-collapse:collapse;font-size:11px;">';
    h+='<tr><td style="padding:2px 6px;color:#555;width:140px;">Carrier / policy</td><td style="padding:2px 6px;">'+esc(h1.carrier||'—')+' · '+esc(h1.policy_number||'—')+'</td></tr>';
    h+='<tr><td style="padding:2px 6px;color:#555;">Insured / NAICS</td><td style="padding:2px 6px;">'+esc(h1.insured||'—')+' · '+esc(nn.code||'—')+' '+esc(((nn.cheatsheet||{}).industry)||nn.vertical||'')
      +(nn.class_code?' <span style="color:#777;">(GL class '+esc(nn.class_code)+')</span>':'')+(h1.square_footage?' · '+esc(h1.square_footage)+' sq ft':'')+'</td></tr>';
    h+='<tr><td style="padding:2px 6px;color:#555;">Premium / term</td><td style="padding:2px 6px;">'+esc(h1.annual_premium||'—')+(h1.policy_term?' · '+esc(h1.policy_term.effective)+' → '+esc(h1.policy_term.expiration):'')+'</td></tr>';
    h+='</table>';
    h+='<div style="font-size:10px;font-style:italic;color:#333;margin:5px 0;">'+esc(h1.bop_definition)+'</div>';
    // NOT COVERED leads the print block for the same reason it leads the panel.
    h+='<div style="border:2px solid #b71c1c;border-radius:5px;margin:8px 0;">';
    h+='<div style="background:#b71c1c;color:#fff;padding:5px 9px;font-weight:700;font-size:12px;">NOT COVERED · Standalone Policies Recommended</div>';
    (S.s5_not_covered||[]).forEach(r=>{ h+='<div style="padding:4px 9px;border-bottom:1px solid #eee;font-size:10px;">'
      +'<b>['+esc(r.criticality)+'] '+esc(r.coverage)+'</b><br>'+esc(r.rationale)
      +'<br><i>'+esc(r.recommendation)+'</i>'+(r.regulatory_driver?'<br><span style="color:#00695c;">'+esc(r.regulatory_driver)+'</span>':'')+'</div>'; });
    h+='</div>';
    const gl=(S.s2_core||{}).general_liability||{}, p=(S.s2_core||{}).property||{};
    const v=x=>(x&&x.value)?esc(x.value):'—';
    h+=sec('BOP CORE');
    h+='<div style="font-size:10px;">Property: Building '+v(p.building)+' · BPP '+v(p.business_personal_property)+' · Ded '+v(p.deductible)
      +'<br>GL: Occ '+v(gl.each_occurrence)+' · Agg '+v(gl.general_aggregate)+' · Prod/CO '+v(gl.products_completed_ops)+' · Med pay '+v(gl.medical_payments)+'</div>';
    const cp=(S.s7_benchmarks||{}).core_parts||[];
    if(cp.length){ h+='<table style="width:100%;border-collapse:collapse;font-size:10px;margin-top:3px;">'
      +cp.map(x=>'<tr><td style="padding:2px 4px;font-weight:600;width:150px;">'+esc(x.part)+'</td><td style="padding:2px 4px;">'+esc(x.detail)+'</td>'
        +'<td style="padding:2px 4px;font-weight:700;color:'+(x.status==='HIGH GAP'?'#b71c1c':(x.status==='BELOW STANDARD'?'#e65100':((x.status==='NOT EVALUATED'||x.status==='PARTIAL')?'#757575':'#2e7d32')))+';">'+esc(x.status)+'</td></tr>').join('')+'</table>'; }
    h+=sec('Elected Add-Ons');
    h+='<div style="font-size:10px;">'+((S.s3_addons_elected||[]).map(a=>esc(a.name)+(a.limit?' ('+esc(a.limit)+')':'')).join(' · ')||'None confirmed')+'</div>';
    h+=sec('Add-Ons NOT Elected');
    h+='<div style="font-size:10px;">'+((S.s4_addons_not_elected||[]).map(a=>'['+esc(a.criticality)+'] '+esc(a.addon)).join(' · ')||'None flagged')+'</div>';
    const rs=(S.s6_restrictions||[]).filter(r=>!r.is_addon);
    if(rs.length){ h+=sec('Coverage Restriction Endorsements'); h+='<table style="width:100%;border-collapse:collapse;font-size:10px;">'
      +rs.map(r=>'<tr><td style="padding:2px 4px;font-weight:600;width:70px;">'+esc(r.form)+'</td><td style="padding:2px 4px;">'+esc(r.name)+'</td><td style="padding:2px 4px;color:#b71c1c;">'+esc(r.impact)+'</td></tr>').join('')+'</table>'; }
    const bm=S.s7_benchmarks||{};
    if((bm.rows||[]).length){ h+=sec('Peer Benchmark Comparison'); h+='<table style="width:100%;border-collapse:collapse;font-size:10px;">'
      +'<tr><td style="padding:2px 4px;color:#555;">Coverage</td><td style="padding:2px 4px;color:#555;">This policy</td><td style="padding:2px 4px;color:#555;">Median</td><td style="padding:2px 4px;color:#555;">75th</td><td style="padding:2px 4px;color:#555;">Status</td></tr>'
      +bm.rows.map(r=>'<tr><td style="padding:2px 4px;">'+esc(r.coverage)+'</td><td style="padding:2px 4px;">'+esc(r.this_policy)+'</td><td style="padding:2px 4px;">'+esc(r.peer_median)+'</td><td style="padding:2px 4px;">'+esc(r.peer_p75)+'</td><td style="padding:2px 4px;font-weight:600;color:'+(r.status==='BELOW STANDARD'?'#b71c1c':'#2e7d32')+';">'+esc(r.status)+'</td></tr>').join('')+'</table>';
      h+='<div style="font-size:9px;color:#999;">'+esc(bm.provenance||'')+'</div>'; }
    h+=sec('Recommended Actions');
    h+='<ol style="margin:2px 0 0 16px;font-size:10px;">'+(S.s10_actions||[]).map(a=>'<li>'+esc(a.action)+' — ['+esc(a.criticality)+']</li>').join('')+'</ol>';
    const src=rec.sources_referenced||{};
    // The same Deep Policy Text Scan section the P&C and EB reports carry, in
    // the same order. The BOP review reads the same uploaded documents, so a
    // policy with D&O or Professional Liability written into another form —
    // or, as often, neither — belongs on this record too.
    {
      const _fd = window._lastFormData;
      if (_fd && _fd.deepScanResults) {
        h+=sec('Deep Policy Text Scan — Embedded Coverage Detection');
        h+=window.spDeepScanSectionHtml(_fd, esc);
      }
    }
    h+='<div style="font-size:9px;color:#999;margin-top:8px;">Compliance Record · '+esc(rec.analysis_version||'')+' · analyzed '+esc((rec.analysis_timestamp||'').slice(0,19))+' UTC · '
      +esc(src.iso_forms||0)+' ISO forms, '+esc(src.carrier_endorsements||0)+' carrier endorsements, '+esc(src.state_regulatory_citations||0)+' state + '+esc(src.federal_regulatory_citations||0)+' federal citations, '
      +esc(src.peer_benchmark_records||0)+' benchmark records · Record ID '+esc(rec.compliance_record_id||'')
      +' (replay: /api/analyze/bop/record/'+esc(rec.compliance_record_id||'')+')</div>';
    h+='</div>';
    return h;
  };
})();
